import React, {ReactElement} from 'react';
import {Card, CardContent, makeStyles, Typography} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import HelperUtils from '@shared/utils/HelperUtils';
import {Subscriber} from '../EligibilityVerificationMapper';
import KeyValueCard from '@components/system/KeyValueCard/KeyValueCard';
import {CARD_BOX_SHADOW} from 'src/style/variable';
import {INSURANCE_SOURCE_MAPPER} from '@shared/constants/Mapping';

const styles = makeStyles(() => ({
  cardRoot: {
    backgroundColor: 'rgba(33, 174, 47, 0.1)',
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    },
    boxShadow: CARD_BOX_SHADOW,
    borderRadius: '1rem'
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 600,
    paddingBlock: '1rem'
  },
  subscriberName: {
    fontSize: '2rem',
    marginBottom: '1rem'
  },
  addressValue: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '0.5rem'
  },
  insuranceSource: {
    padding: '1rem',
    fontStyle: 'italic'
  }
}));

interface InsuranceSubscriberDetailsCardInterface {
  subscriber: Subscriber;
  insuranceSource: string;
}

const InsuranceSubscriberDetailsCard = (
  props: InsuranceSubscriberDetailsCardInterface
): ReactElement => {
  const classes = styles();
  const {subscriber, insuranceSource} = props;
  const {t} = useTranslation();

  const addressItems = [];
  if (subscriber?.address?.line1) {
    addressItems.push(
      <Typography key="line1">{subscriber.address?.line1}</Typography>
    );
  }
  if (subscriber?.address?.line2) {
    addressItems.push(
      <Typography key="line2">{subscriber.address?.line2}</Typography>
    );
  }
  if (subscriber?.address?.city) {
    addressItems.push(
      <Typography key="city">{subscriber.address?.city}</Typography>
    );
  }
  if (
    subscriber?.address?.state ||
    subscriber?.address?.stateCode ||
    subscriber?.address?.zipCode
  ) {
    addressItems.push(
      <Typography>
        {subscriber?.address?.state ? `${subscriber?.address?.state}, ` : ''}
        {subscriber?.address?.stateCode
          ? `${subscriber?.address?.stateCode}, `
          : ''}
        {subscriber?.address?.zipCode}
      </Typography>
    );
  }

  const getInsuranceSourceLabel = (source: string) => {
    if (source) {
      if (source in INSURANCE_SOURCE_MAPPER)
        return t(
          INSURANCE_SOURCE_MAPPER[
            source as keyof typeof INSURANCE_SOURCE_MAPPER
          ].label
        );
      else return source;
    }
    return '';
  };

  const subscriberData = [
    {
      label: t('ELIGIBILITY_VERIFICATION.MEMBER_ID'),
      value: subscriber?.memberId ? subscriber.memberId : '-'
    },
    // NOTE: Commented ONLY For DEMO Purpose, Once demo is done, need to uncomment
    // {
    //   label: t('ELIGIBILITY_VERIFICATION.GENDER'),
    //   value: subscriber?.gender ? subscriber.gender : '-'
    // },
    {
      label: t('ELIGIBILITY_VERIFICATION.CASE_NUMBER'),
      value: subscriber?.caseNumber ? subscriber?.caseNumber : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.ADDRESS'),
      value: (
        <Typography className={classes.addressValue}>
          {addressItems.length > 0 ? addressItems : '-'}
        </Typography>
      )
    }
  ];

  return (
    <div className="section-header">
      <Typography className={classes.title}>
        {t('ELIGIBILITY_VERIFICATION.SUBSCRIBER')}
      </Typography>
      <Card className={`${classes.cardRoot}`}>
        <CardContent>
          <Typography className={classes.subscriberName}>
            {subscriber?.firstName
              ? HelperUtils.formFullName(
                  subscriber?.firstName,
                  subscriber?.lastName,
                  subscriber?.middleName
                )
              : '-'}
          </Typography>
          <div>
            <KeyValueCard data={subscriberData} />
          </div>
        </CardContent>
        {insuranceSource && (
          <p className={classes.insuranceSource}>
            *{t('ELIGIBILITY_VERIFICATION.INSURANCE_SOURCE')}:{' '}
            {getInsuranceSourceLabel(insuranceSource)}
          </p>
        )}
      </Card>
    </div>
  );
};

export default InsuranceSubscriberDetailsCard;
